import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";

// Wraps any route. Bounces to /login if signed-out, /portal if a client tries
// to hit an admin-only page, and /studio if an admin hits a client-only page.
//
// Exception: client-role users with `linked_association_names` are scoped
// admins for those associations — they get read access to the association
// & performance pages so they can see their own clips. Any other admin-only
// route still bounces them to /portal.
export function ProtectedRoute({ children, adminOnly = false, clientOnly = false }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-stone-500">…</div>;
  }
  if (!user) {
    return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  }
  if (adminOnly && user.role !== "admin") {
    // Scoped-admin escape hatch: association-linked clients may view the
    // association detail + any performance page. Backend enforces which
    // performances they can actually read.
    const linked = (user.linked_association_names || []).length > 0;
    const p = loc.pathname || "";
    const scopedOk = linked && (
      p.startsWith("/associations/") ||
      p.startsWith("/performances/") ||
      p.startsWith("/events/")
    );
    if (!scopedOk) return <Navigate to="/portal" replace />;
  }
  if (clientOnly && user.role === "admin") return <Navigate to="/" replace />;
  return children;
}
